import { engineComponent, ArrayUtils } from "neo-common";
import { Communication } from "neo-games";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { Symbols } from "./Symbols";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../Utils/FrameTimer";
import { Configuration } from "../Components/Configuration";
import { ComponentTypes } from "../Components/ComponentTypes";
import { PlayList } from "../Components/Sounds/PlayList";
import { AppContext } from "../../Common/AppContext";
import { GameData } from "../Channels/Common/Datas/GameData";
import { DataManager } from "../Managers/DataManager";
import { ModuleManager } from "../Managers/ModuleManager";
import { Reel } from "./Reel";
import { ReelExtended } from "../CustomComponents/ReelExtended";
import { CustomUtils } from "../Utils/CustomUtils";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";

type IReel = Communication.Game.IReel;

/**
 * Machine handle reels and symbols for reels rotation.
 * Machine define behaviour for complete flow of reel rotation.
 * @class Machine
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class Machine extends BaseElement {
    // events for view when reel or machine stopped
    static MachineStopped: string = "machineStopped";
    static ReelStopped: string = "onReelStopped";

    static Respin: boolean = false;

    // symbols from view
    protected _symbols: Symbols;
    // array of reel indexes
    protected _reels: Array<ReelExtended>;
    // array of stopped symbols for finish reel rotation
    protected _stoppedSymbolsArray: Array<IReel>;

    // count of reel
    protected _reelCount: number;
    // reels x positions
    protected _reelX: Array<number>;
    // reels y positions
    protected _reelY: Array<number>;
    // symbol width without offsetX
    protected _symbolWidth: number;
    // symbol height without offsetY
    protected _symbolHeight: number;
    // number of symbols which will be visible on screen during the rotation
    protected _visibleSymbolCount: number;
    // number of symbols under visible symbols
    private _symbolTopCount: number;
    // number of symbols below visible symbols
    private _symbolBottomCount: number;
    // min spin rotation in frames
    protected _minSpinRotation: number;
    // min spin rotation in frames for high wins
    protected _minSpinRotationLong: number;

    // delay to start rotation in frames
    protected _startDelay: number;
    // delay to stop rotation in frames
    protected _stopDelay: number;
    // configuration ID for reels
    protected _reelConfiguration: string;

    // timer for minimal time to stop reels
    protected _stopTimer: FrameTimerEventEmitter;
    // timer for defined time in configuration.json to stop reels
    protected _legalStopTimer: number;

    // boolean which indicates that machine can stop and rotate min defined time
    protected _stoppedFromMachine: boolean;
    // boolean which indicates that machine can stop and response has arrived
    protected _stoppedFromServer: boolean;
    // boolean which indicates that machine can stop and rotate min legal time
    protected _stoppedFromLegal: boolean;

    protected _startSpinTime: number;

    // container as a parent for reels
    protected _reelsContainer: PIXI.Container;

    // helper to count stopped reels
    protected _stoppedReelIndexes: number;

    // information for view which reel was stopped actualy
    actualReelStopped: number;

    constructor() {
        super();

        // default values
        this._reels = [];
        this._stoppedSymbolsArray = [];
        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;
        this._stoppedReelIndexes = 0;

        this._stopTimer = CreateFrameTimer("Machine");
        this._stopTimer.on(FrameTimerEvents.Complete, this.spinStop.bind(this));

        this.actualReelStopped = -1;

        this._symbolTopCount = 1;
        this._symbolBottomCount = 1;

        this._reelsContainer = new PIXI.Container();
        this.addChild(this._reelsContainer);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public create(node: Element): void {
        super.create(node);

        if (this.maskSprite != null && this._reelsContainer != null) {
            this._reelsContainer.addChild(this.maskSprite);
            this._reelsContainer.mask = this.maskSprite;
        }

        if (node.hasAttribute(MachineAttributes.CONFIGS)) {
            let configs: Array<string> = node.getAttribute(MachineAttributes.CONFIGS).split(";");
            for (let i: number = 0; i < configs.length; i++) {
                let config: Element = Configuration.Configurations[configs[i]];
                if (config != null) {
                    this.configurate(config);
                }
            }
        }
    }

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    protected configurate(node: Element): void {
        /*
         * ReelCount is count of reels on screen for game.
         * @property ReelCount
         * @type Number
         * @default null
         */
        if (node.hasAttribute("ReelCount")) {
            this._reelCount = parseInt(node.getAttribute("ReelCount"));
        }

        /*
         * ReelX is array of x position for reels.
         * @property ReelX
         * @type Array<number>
         * @default null
         */
        if (node.hasAttribute("ReelX")) {
            this._reelX = ArrayUtils.numberArray(node.getAttribute("ReelX"));
        }

        /*
         * ReelY is array of y position for reels.
         * @property ReelY
         * @type Array<number>
         * @default null
         */
        if (node.hasAttribute("ReelY")) {
            this._reelY = ArrayUtils.numberArray(node.getAttribute("ReelY"));
        }

        /*
         * Standard symbol width without offsets.
         * @property SymbolWidth
         * @type number
         * @default null
         */
        if (node.hasAttribute("SymbolWidth")) {
            this._symbolWidth = parseInt(node.getAttribute("SymbolWidth"));
        }

        /*
         * Standard symbol height without offsets.
         * @property SymbolHeight
         * @type number
         * @default null
         */
        if (node.hasAttribute("SymbolHeight")) {
            this._symbolHeight = parseInt(node.getAttribute("SymbolHeight"));
        }

        /*
         * Number of symbols visible on reel.
         * @property VisibleSymbolCount
         * @type number
         * @default null
         */
        if (node.hasAttribute("VisibleSymbolCount")) {
            this._visibleSymbolCount = parseInt(node.getAttribute("VisibleSymbolCount"));
        }

        if (node.hasAttribute("SymbolTopCount")) {
            this._symbolTopCount = parseInt(node.getAttribute("SymbolTopCount"));
        }

        if (node.hasAttribute("SymbolBottomCount")) {
            this._symbolBottomCount = parseInt(node.getAttribute("SymbolBottomCount"));
        }

        /*
         * Min spin rotation in frames.
         * @property MinSpinRotation
         * @type number
         * @default 40
         */
        if (node.hasAttribute("MinSpinRotation")) {
            this._minSpinRotation = parseInt(node.getAttribute("MinSpinRotation"));
        } else {
            this._minSpinRotation = 40;
        }

        /*
         * Min spin rotation in frames for outro high wins.
         * @property MinSpinRotationLong
         * @type number
         * @default 100
         */
        if (node.hasAttribute("MinSpinRotationLong")) {
            this._minSpinRotationLong = parseInt(node.getAttribute("MinSpinRotationLong"));
        } else {
            this._minSpinRotationLong = 100;
        }

        /*
         * Delay to start rotation in frames.
         * @property StartDelay
         * @type number
         * @default 3
         */
        if (node.hasAttribute("StartDelay")) {
            this._startDelay = parseInt(node.getAttribute("StartDelay"));
        } else {
            this._startDelay = 3;
        }

        /*
         * Delay to stop rotation in frames.
         * @property StopDelay
         * @type number
         * @default 10
         */
        if (node.hasAttribute("StopDelay")) {
            this._stopDelay = parseInt(node.getAttribute("StopDelay"));
        } else {
            this._stopDelay = 10;
        }

        /*
         * Configuration ID for reels.
         * @property StopDelay
         * @type string
         * @default null
         */
        if (node.hasAttribute("ReelConfiguration")) {
            this._reelConfiguration = node.getAttribute("ReelConfiguration");
        }
    }

    /**
     * Create reels based on response and xml configuration.
     * @method createReels
     * @param reels Array<Array<number>> from initialization and based on response
     * @return void
     */
    protected createReels(reels: Array<Array<number>>): void {
        for (let i: number = 0; i < this._reelCount; i++) {
            this._reels.push(new ReelExtended());
        }

        for (let i: number = 0; i < this._reelCount; i++) {
            const reel = this._reels[i];

            if (this._reelConfiguration != null) {
                let configs: Array<string> = this._reelConfiguration.split(";");
                for (let j: number = 0; j < configs.length; j++) {
                    reel.configure(Configuration.Configurations[configs[j]]);
                }
            }
            reel.visible = false;
            reel.initialize(
                i > 0 ? this._reels[i - 1] : null,
                i < this._reelCount - 1 ? this._reels[i + 1] : null,
                i,
                this._symbols,
                reels[i],
                this._reelX[i],
                this._reelY[i],
                this._visibleSymbolCount,
                this._symbolTopCount,
                this._symbolBottomCount,
                this._symbolWidth,
                this._symbolHeight,
                this._startDelay * i,
                this._stopDelay * i,
            );
            reel.on(Reel.ReelStopped, this.onReelStopped.bind(this));
            this._reelsContainer.addChild(reel);
        }
        this._reelsContainer.visible = false;
    }

    /**
     * Initialize reference correct symbols and create reels.
     * @method initialize
     * @param symbols Symbols
     * @param reels Array<Array<number>>
     * @return void
     */
    public initialize(symbols: Symbols, reels: Array<Array<number>>): void {
        this._symbols = symbols;
        this.createReels(reels);
    }

    /**
     * Set symbols which stopped after reels will be stopped.
     * @method setStoppedSymbolsArray
     * @param value Array<number>
     * @return void
     */
    public setStoppedSymbolsArray(value: Array<IReel>): void {
        this._stoppedSymbolsArray = value;
    }

    /**
     * Show static symbols on reels.
     * @method showSymbols
     * @param reelsData Array<IReel>
     * @return void
     */
    public showSymbols(reelsData: Array<IReel>): void {
        if (DEBUG) {
            this.log("showSymbols");
        }

        this.actualReelStopped = -1;

        if (this._reels != null) {
            for (let i: number = 0; i < this._reelCount; i++) {
                if (reelsData[i] != null) {
                    if (this._reels[i] != null) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(reelsData[i]);
                        this._reels[i].showSymbols();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reelsData with index: " + i);
                    }
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }
    }

    /**
     * Start spinning reels.
     * @method spinStart
     * @param longRotation boolean
     * @return void
     */
    public spinStart(longRotation?: boolean): void {
        if (DEBUG) {
            this.log("spinStart");
        }

        Machine.Respin = false;

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._startSpinTime = Date.now();

        this.visible = true;
        this._reelsContainer.visible = true;

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        if (this._reels != null) {
            for (let i: number = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);
                        this._reels[i].showSymbols();
                        this._reels[i].spinStart();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            if (longRotation != null && longRotation) {
                this._stopTimer.start(this._minSpinRotationLong);
            } else {
                this._stopTimer.start(this._minSpinRotation);
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }

        PlayList.play("SND_BaseRotationStart");
        PlayList.loop("SND_BaseRotation", 0, true);
    }

    protected verifyMinGameRoundDurationConditions(): boolean {
        return (
            (AppContext.gameConfiguration.MinGameRoundDuration > 0 ||
                AppContext.gameConfiguration.GameRoundMinSpan > 0 ||
				(FreeRoundBonus.inFrb && AppContext.gameConfiguration.FRBGameRoundMinSpan > 0)) &&
            !CustomUtils.isRespinActive() &&
            !CustomUtils.isRespinLast()
        );
    }

    /**
     * Stop reels rotation.
     * @method spinStop
     * @param event any - from timer
     * @param force boolean - default false used when spin_stop was dispatch
     * @param fromServer boolean - default false used when spin_stop was dispatch from server
     * @param legal
     * @return void
     */
    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (DEBUG) {
            this.log("spinStop: " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (!Machine.Respin && !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }
            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);
                            this._reels[i].spinStop(force);
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    public spinReset(): void {
        if (this._reels != null) {
            for (let i: number = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    this._reels[i].spinReset();
                }
            }
        }

        this._reelsContainer.visible = false;
    }

    /**
     * Get remaining time for legal spin duration
     * @method getLegalRemainingSpinDuration
     * @return number
     */
    protected getLegalRemainingSpinDuration(): number {
        let duration = 0;

        if (AppContext.gameConfiguration.GameRoundMinSpan > 0) {
            duration = this.calculateRemainingSpinDuration(AppContext.gameConfiguration.GameRoundMinSpan);
        }

        if (AppContext.gameConfiguration.MinGameRoundDuration > 0) {
            duration = AppContext.gameConfiguration.MinGameRoundDuration;
        }

        if (AppContext.gameConfiguration.FRBGameRoundMinSpan > 0 && FreeRoundBonus.inFrb) {
            duration = this.calculateRemainingSpinDuration(
                Math.max(
                    AppContext.gameConfiguration.GameRoundMinSpan,
                    AppContext.gameConfiguration.FRBGameRoundMinSpan,
                ),
            );
        }

        return duration;
    }

    /**
     * Calculate remaining time for legal spin duration
     * @method calculateRemainingSpinDuration
     * @param minTime: number - minimal spin duration in seconds
     * @return number
     */
    protected calculateRemainingSpinDuration(minTime: number): number {
        const now = Date.now();
        const diff = now - this._startSpinTime;
        const reelStopDuration = this.getReelStopDuration();
        return Math.max(minTime - diff - reelStopDuration, 0);
    }

    protected getReelStopDuration(): number {
        return (this._reels[this._reelCount - 1].getStopDuration() / 60) * 1000;
    }

    /**
     * Dispatched onMachineStopped when all reels are stopped.
     * @method onReelStopped
     * @param reelIndex number - reel index
     * @return void
     */
    public onReelStopped(reelIndex: number): void {
        if (DEBUG) {
            this.log("reel stopped: " + reelIndex);
        }

        PlayList.play("SND_BaseRotationEnd" + reelIndex);

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;
        this.emit(Machine.ReelStopped);

        if (this._stoppedReelIndexes === this._reelCount) {
            PlayList.stop("SND_BaseRotation", 10);
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stopTimer.stop();
            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this.emit(Machine.MachineStopped);
            this._reelsContainer.visible = false;
        }
    }
}

/**
 * Inherits attributes from BaseElement.
 */
export const MachineAttributes = {
    /**
     * Name of config for machine.
     * @property Configs
     * @type string
     * @default null
     */
    CONFIGS: "Configs",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Machine.ts
 **/